import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import { manifestApi } from '../../services/api';
import { fmtDT, fmtPKR } from '../../utils/helpers';
import { Spinner, EmptyState, Modal } from '../../components/common';
import toast from 'react-hot-toast';
import { Plus, Eye, Lock, Truck } from 'lucide-react';

const STATUS_COLORS = {
  DRAFT: 'badge-warning', SEALED: 'badge-navy',
  DEPARTED: 'badge-info', DELIVERED: 'badge-success', CANCELLED: 'badge-gray'
};

export default function ManifestsPage() {
  const [manifests, setManifests] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [detail, setDetail]       = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [filters, setFilters]     = useState({ status: '', routeType: '' });

  // Create form
  const [createForm, setCreateForm] = useState({
    routeType: 'DELIVERY', driverName: '', vehiclePlate: '', plannedDeparture: '',
    trackingIds: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await manifestApi.getAll({
        status:    filters.status    || undefined,
        routeType: filters.routeType || undefined,
      });
      if (res.success) setManifests(res.data);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (m) => {
    setSelected(m);
    const res = await manifestApi.getOne(m.manifest_id);
    if (res.success) setDetail(res.data);
  };

  const handleCreate = async () => {
    const tids = createForm.trackingIds
      .split(/[\n,\s]+/)
      .map(t => t.trim().toUpperCase())
      .filter(Boolean);

    if (!tids.length) { toast.error('Add at least one tracking ID'); return; }

    try {
      const res = await manifestApi.create({
        routeType:        createForm.routeType,
        plannedDeparture: createForm.plannedDeparture || undefined,
        trackingIds:      tids,
      });
      if (res.success) {
        toast.success(`Manifest ${res.data.code} created with ${tids.length} shipments`);
        setShowCreate(false);
        setCreateForm({ routeType:'DELIVERY', driverName:'', vehiclePlate:'', plannedDeparture:'', trackingIds:'' });
        load();
      }
    } catch (e) { toast.error(e.message || 'Create failed'); }
  };

  const handleSeal = async (manifestId) => {
    const sealNo = prompt('Enter seal number:');
    if (!sealNo) return;
    try {
      const res = await manifestApi.seal(manifestId, { sealNo });
      if (res.success) {
        toast.success('Manifest sealed — all shipments set to IN_TRANSIT');
        load();
        setSelected(null);
        setDetail(null);
      }
    } catch (e) { toast.error(e.message || 'Seal failed'); }
  };

  const addItem = async (manifestId) => {
    const tid = prompt('Enter Tracking ID to add:');
    if (!tid) return;
    try {
      await manifestApi.addItem(manifestId, { trackingId: tid.trim().toUpperCase() });
      toast.success('Item added');
      const res = await manifestApi.getOne(manifestId);
      if (res.success) setDetail(res.data);
    } catch (e) { toast.error(e.message || 'Failed to add'); }
  };

  return (
    <AppShell title="Manifests & Dispatch"
      actions={
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
          <Plus size={14} /> Create Manifest
        </button>
      }>

      {/* Filters */}
      <div className="filters-bar">
        <select className="form-control" style={{ width: 160 }} value={filters.status}
          onChange={e => setFilters(f => ({...f, status: e.target.value}))}>
          <option value="">All Statuses</option>
          {['DRAFT','SEALED','DEPARTED','DELIVERED','CANCELLED'].map(s =>
            <option key={s} value={s}>{s}</option>
          )}
        </select>
        <select className="form-control" style={{ width: 160 }} value={filters.routeType}
          onChange={e => setFilters(f => ({...f, routeType: e.target.value}))}>
          <option value="">All Types</option>
          {['DELIVERY','PICKUP','TRANSIT','RTO'].map(t =>
            <option key={t} value={t}>{t}</option>
          )}
        </select>
        <button className="btn btn-outline btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : manifests.length ? (
            <table>
              <thead>
                <tr>
                  <th>Manifest Code</th><th>Type</th><th>Driver</th>
                  <th>Vehicle</th><th>Shipments</th><th>COD Total</th>
                  <th>Departure</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {manifests.map(m => (
                  <tr key={m.manifest_id}>
                    <td><code style={{ fontSize: '.8rem', color: 'var(--navy)', fontWeight: 700 }}>{m.code}</code></td>
                    <td><span className="badge badge-info">{m.route_type}</span></td>
                    <td>{m.driver_name || <span className="text-muted">—</span>}</td>
                    <td>{m.vehicle_plate || <span className="text-muted">—</span>}</td>
                    <td style={{ fontWeight: 600 }}>{m.total_shipments}</td>
                    <td>{m.total_cod > 0 ? fmtPKR(m.total_cod) : '—'}</td>
                    <td className="text-sm">{fmtDT(m.planned_departure)}</td>
                    <td><span className={`badge ${STATUS_COLORS[m.status] || 'badge-gray'}`}>{m.status}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-outline btn-sm" onClick={() => openDetail(m)}>
                          <Eye size={12} /> View
                        </button>
                        {m.status === 'DRAFT' && (
                          <button className="btn btn-primary btn-sm" onClick={() => handleSeal(m.manifest_id)}>
                            <Lock size={12} /> Seal
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="📋" title="No manifests found" message="Create a manifest to assign shipments to a vehicle." />}
        </div>
      </div>

      {/* Detail modal */}
      <Modal open={!!selected} onClose={() => { setSelected(null); setDetail(null); }}
        title={`Manifest: ${selected?.code}`} size="modal-lg">
        {detail ? (
          <>
            <div className="modal-body">
              {/* Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20,
                padding: 14, background: 'var(--gray-50)', borderRadius: 8 }}>
                <InfoBox label="Type"       v={detail.manifest.route_type} />
                <InfoBox label="Status"     v={<span className={`badge ${STATUS_COLORS[detail.manifest.status]}`}>{detail.manifest.status}</span>} />
                <InfoBox label="Shipments"  v={detail.manifest.total_shipments} />
                <InfoBox label="Seal No."   v={detail.manifest.seal_no || '—'} />
                <InfoBox label="Driver"     v={detail.manifest.driver_name || '—'} />
                <InfoBox label="Vehicle"    v={detail.manifest.vehicle_plate || '—'} />
                <InfoBox label="Hub"        v={detail.manifest.hub_name || '—'} />
                <InfoBox label="Departure"  v={fmtDT(detail.manifest.planned_departure)} />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {detail.manifest.status === 'DRAFT' && (
                  <>
                    <button className="btn btn-primary btn-sm" onClick={() => addItem(detail.manifest.manifest_id)}>
                      <Plus size={12} /> Add Shipment
                    </button>
                    <button className="btn btn-amber btn-sm" onClick={() => handleSeal(detail.manifest.manifest_id)}>
                      <Lock size={12} /> Seal & Dispatch
                    </button>
                  </>
                )}
                <button className="btn btn-outline btn-sm" onClick={() => {
                  const content = detail.items.map((item, i) =>
                    `${i+1}. ${item.tracking_id} | ${item.service_type} | ${item.weight_actual}kg | COD:${item.cod_amount||0} | ${item.shipment_status}`
                  ).join('\n');
                  const w = window.open('', '_blank');
                  w.document.write(`<pre style="font-family:monospace;font-size:12px;padding:20px">MANIFEST: ${detail.manifest.code}\nRoute: ${detail.manifest.route_type}\nDriver: ${detail.manifest.driver_name||'—'}\nVehicle: ${detail.manifest.vehicle_plate||'—'}\nSeal: ${detail.manifest.seal_no||'DRAFT'}\n\n${content}</pre>`);
                  w.print();
                }}>
                  🖨️ Print
                </button>
              </div>

              {/* Items table */}
              <div className="table-wrap" style={{ maxHeight: 360, overflowY: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>#</th><th>Tracking ID</th><th>Service</th>
                      <th>Weight</th><th>COD</th><th>Destination</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.items.map((item, i) => (
                      <tr key={item.item_id}>
                        <td style={{ color: 'var(--gray-400)', fontSize: '.78rem' }}>{i+1}</td>
                        <td><code style={{ fontSize: '.78rem', color: 'var(--navy)' }}>{item.tracking_id}</code></td>
                        <td className="text-sm">{item.service_type}</td>
                        <td className="text-sm">{item.weight_actual} kg</td>
                        <td className="text-sm">{item.cod_amount > 0 ? fmtPKR(item.cod_amount) : '—'}</td>
                        <td className="text-sm truncate" style={{ maxWidth: 160 }}>
                          {item.delivery_address ? (() => { try { const a = typeof item.delivery_address === 'string' ? JSON.parse(item.delivery_address) : item.delivery_address; return `${a.city||''}`; } catch { return '—'; } })() : '—'}
                        </td>
                        <td><span className="badge badge-info" style={{ fontSize: '.65rem' }}>{item.shipment_status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : <div className="loading-center" style={{ height: 200 }}><div className="spinner" /></div>}
      </Modal>

      {/* Create modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create New Manifest">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Route Type <span className="req">*</span></label>
              <select className="form-control" value={createForm.routeType}
                onChange={e => setCreateForm(f => ({...f, routeType: e.target.value}))}>
                <option value="DELIVERY">Delivery</option>
                <option value="PICKUP">Pickup</option>
                <option value="TRANSIT">Transit</option>
                <option value="RTO">RTO (Return)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Planned Departure</label>
              <input className="form-control" type="datetime-local" value={createForm.plannedDeparture}
                onChange={e => setCreateForm(f => ({...f, plannedDeparture: e.target.value}))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">
              Tracking IDs <span className="req">*</span>
              <span style={{ fontSize: '.72rem', color: 'var(--gray-400)', marginLeft: 6 }}>
                One per line, or comma-separated
              </span>
            </label>
            <textarea className="form-control" rows={8}
              placeholder="ECLPK202604120000001&#10;ECLPK202604120000002&#10;ECLPK202604120000003"
              style={{ fontFamily: 'monospace', fontSize: '.82rem' }}
              value={createForm.trackingIds}
              onChange={e => setCreateForm(f => ({...f, trackingIds: e.target.value}))} />
            <div style={{ fontSize: '.75rem', color: 'var(--gray-400)', marginTop: 4 }}>
              {createForm.trackingIds.split(/[\n,\s]+/).filter(Boolean).length} tracking IDs entered
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={() => setShowCreate(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate}>
            <Truck size={14} /> Create Manifest
          </button>
        </div>
      </Modal>
    </AppShell>
  );
}

function InfoBox({ label, v }) {
  return (
    <div>
      <div style={{ fontSize: '.68rem', color: 'var(--gray-400)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '.875rem', fontWeight: 600 }}>{v}</div>
    </div>
  );
}
